import {JourneyStepData} from "@/domains/-journey-steps/types.ts";
import JourneyStep from "@/domains/-journey/models/JourneyStep.ts";
import {PersonRoleCoachee} from "@/domains/-workspace/models/PersonRoleCoachee";
import {dateFromServer} from "@/functions/dates.ts";
import {
  GetJourneyStepProps,
  GetPersonJourney,
  JourneyId,
  JourneyName,
  JourneySchema,
  JourneyStepGroup,
  JourneyStepId,
  JourneyStepName,
  JourneyStepType,
  PersonId,
  Version,
  WorkspaceId
} from "@/sdk";


function isMissingExternError(error: unknown): boolean {
  return typeof error === 'object'
    && error !== null
    && 'response' in error
    && typeof error.response === 'object'
    && error.response !== null
    && 'data' in error.response
    && typeof error.response.data === 'object'
    && error.response.data !== null
    && 'detail' in error.response.data
    && error.response.data.detail === 'journey.no_extern';
}


export default class Journey {
  readonly public_id: JourneyId;
  readonly name: JourneyName;
  readonly person_id: PersonId;
  readonly workspace_id: WorkspaceId;
  readonly created_at: Date;
  readonly version: Version;
  readonly steps: {
    [key in JourneyStepId]?: JourneyStep;
  }
  readonly alias: string | undefined;
  readonly schema: JourneySchema;
  readonly coachee: PersonRoleCoachee;
  readonly extern_pid: undefined | PersonId;
  updated_at: Date | null;
  is_closed: boolean;


  constructor(workspaceId: WorkspaceId, props: GetPersonJourney.Result, coacheeRole: PersonRoleCoachee) {
    this.public_id = props.public_id;
    this.workspace_id = workspaceId;
    this.name = props.name;
    this.created_at = dateFromServer(props.created_at);
    this.updated_at = props.updated_at ? dateFromServer(props.updated_at) : null;
    this.is_closed = props.is_closed;
    this.steps = {};
    this.schema = props.journey_schema;
    this.version = props.journey_schema.version;
    this.coachee = coacheeRole
    this.extern_pid = props.extern_pid

    Object
      .entries(props.journey_schema.steps)
      .forEach(([stepId, stepMeta]) => {

        const stepData: GetJourneyStepProps | undefined = props.steps.find(stepData => stepData.name === stepId);
        this.steps[stepId] = new JourneyStep(this, stepId as JourneyStepId, stepMeta, stepData);
      })
  }

  hasStepById(stepName: JourneyStepId): boolean {
    return Object.hasOwn(this.steps, stepName)
  }

  getStepById(stepName: JourneyStepId): JourneyStep | null {
    return this.hasStepById(stepName) ? this.steps[stepName] as JourneyStep : null
  }

  getStepByName<Name extends JourneyStepName>(stepName: Name): undefined | JourneyStep<JourneyStepData[Name]> {
    return Object.values(this.steps).find(step => step.name === stepName) as undefined | JourneyStep<JourneyStepData[Name]>;
  }

  getStep(props: {
    id?: JourneyStepId;
    name?: JourneyStepName;
    type?: JourneyStepType;
    group?: JourneyStepGroup;
  }): JourneyStep | null {
    let steps = Object.values(this.steps);

    if (props.id)
      steps = steps.filter(step => step.id === props.id)
    if (props.group)
      steps = steps.filter(step => step.group === props.group)
    if (props.type)
      steps = steps.filter(step => step.type === props.type)
    if (props.name)
      steps = steps.filter(step => step.name === props.name)

    if (steps.length === 0) return null

    return steps[0]
  }

  get experienceReportSteps(): JourneyStep[] {
    return Object.values(this.steps)
      .filter(step => step.group === 'experience')
      .filter(step => step.type === 'report')
  }

  get impactReportSteps(): JourneyStep[] {
    return Object.values(this.steps)
      .filter(step => step.group === 'impact')
      .filter(step => step.type === 'report')
  }

  get personSteps(): JourneyStep[] {
    return Object.values(this.steps)
      .filter(step => step.group === 'experience')
      .filter(step => step.type === 'survey' || step.type === 'game')
  }

  get impactPersonSteps(): JourneyStep[] {
    return Object.values(this.steps)
      .filter(step => step.group === 'impact')
      .filter(step => step.type === 'survey' || step.type === 'game')
  }

  get personStepsTotal(): number {
    return this.personSteps.length
  }

  get personStepsDone(): number {
    return this.personSteps.filter(step => step.is_finished).length
  }

  get allPersonStepsFinished(): boolean {
    return this.personSteps.every(step => step.is_finished)
  }

  get labelKey(): string {
    if (this.name == 'feta') {
      if (this.getStep({id: 'feta_ascent_threshold'})?.is_finished)
        return 'feta_ascent'
      else if (this.getStep({id: 'feta_launch_threshold'})?.is_finished)
        return 'feta_launch'
      else
        return 'feta_ignition'

    }
    return this.name
  }

  get labelKeyShort(): string {
    if (this.name == 'feta') {
      if (this.getStep({id: 'feta_ascent_threshold'})?.is_finished)
        return 'feta_ascent_short'
      else if (this.getStep({id: 'feta_launch_threshold'})?.is_finished)
        return 'feta_launch_short'
      else
        return 'feta_ignition_short'

    }
    return this.name+'_short'
  }
}
